import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X, ShieldCheck, DoorOpen } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { PageHeader, SectionCard, Loading, EmptyState } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { formatRupiah, formatTanggalWaktu, formatTanggal, APPROVAL_TYPE } from "@/lib/format";
import { toast } from "sonner";

function Diff({ before, proposed }) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(proposed || {})]));
  const fmt = (v) => {
    if (v == null) return "-";
    if (typeof v === "object") return JSON.stringify(v);
    if (typeof v === "number") return v >= 1000 ? formatRupiah(v) : String(v);
    return String(v);
  };
  return (
    <div className="grid grid-cols-2 gap-3 text-xs">
      <div className="rounded-lg border border-border bg-muted/40 p-3">
        <div className="mb-1 font-semibold text-muted-foreground">Sebelum</div>
        {keys.map((k) => <div key={k}><span className="text-muted-foreground">{k}: </span><span className="font-medium">{fmt((before || {})[k])}</span></div>)}
      </div>
      <div className="rounded-lg border p-3" style={{ borderColor: "#f7cfc2", backgroundColor: "#fdf2ee" }}>
        <div className="mb-1 font-semibold" style={{ color: "#c2410c" }}>Usulan</div>
        {keys.map((k) => <div key={k}><span className="text-muted-foreground">{k}: </span><span className="font-medium">{fmt((proposed || {})[k])}</span></div>)}
      </div>
    </div>
  );
}

export default function Persetujuan() {
  const qc = useQueryClient();
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: async () => (await api.get("/approvals", { params: { status: "pending" } })).data,
  });
  const { data: history = [] } = useQuery({
    queryKey: ["approvals", "all"],
    queryFn: async () => (await api.get("/approvals")).data,
  });

  const approve = useMutation({
    mutationFn: async (id) => (await api.post(`/approvals/${id}/approve`)).data,
    onSuccess: (res) => {
      if (res.status === "approved") toast.success("Pengajuan disetujui & diterapkan.");
      else toast.info(res.detail || "Pengajuan sudah diproses.");
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const reject = useMutation({
    mutationFn: async ({ id, reason }) => (await api.post(`/approvals/${id}/reject`, { reason })).data,
    onSuccess: () => { toast.success("Pengajuan ditolak."); setRejectTarget(null); setRejectReason(""); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const { data: conflicts = [] } = useQuery({
    queryKey: ["exit-conflicts"],
    queryFn: async () => (await api.get("/exit-conflicts")).data,
  });
  const resolveConflict = useMutation({
    mutationFn: async ({ id, decision }) => (await api.post(`/exit-conflicts/${id}/resolve`, { decision })).data,
    onSuccess: (res) => { toast.success(res.status === "resolved" ? "Konflik diselesaikan: keluar diproses & perpanjangan dibatalkan." : "Konflik ditolak."); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const pendingConflicts = conflicts.filter((c) => c.status === "pending");

  const Row = ({ a, actions }) => (
    <div className="rounded-xl border border-border p-4" data-testid={`approval-row-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{APPROVAL_TYPE[a.type] || a.type}</span>
            <StatusBadge kind="approval" value={a.status} />
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Diajukan oleh {a.requested_by} · {formatTanggalWaktu(a.requested_at)}
          </div>
          <div className="mt-1 text-sm"><span className="text-muted-foreground">Alasan: </span>{a.reason}</div>
        </div>
        {actions}
      </div>
      <div className="mt-3"><Diff before={a.before} proposed={a.proposed} /></div>
      {a.decided_by && (
        <div className="mt-2 text-xs text-muted-foreground">
          Diputuskan oleh {a.decided_by} · {formatTanggalWaktu(a.decided_at)}
          {a.decision_reason ? ` · ${a.decision_reason}` : ""}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Persetujuan" description="Setujui atau tolak perubahan data keuangan. Perubahan diterapkan atomik setelah disetujui." />

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Menunggu ({pending.length})</TabsTrigger>
          <TabsTrigger value="conflicts" data-testid="tab-conflicts">Konflik Keluar ({pendingConflicts.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Riwayat</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {isLoading ? <Loading /> : pending.length === 0 ? (
            <EmptyState title="Tidak ada pengajuan menunggu" icon={ShieldCheck} />
          ) : (
            <div className="space-y-3">
              {pending.map((a) => (
                <Row key={a.id} a={a} actions={
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve.mutate(a.id)} disabled={approve.isPending} data-testid={`approve-change-button-${a.id}`}>
                      <Check className="mr-1 h-4 w-4" /> Setujui
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => setRejectTarget(a)} data-testid={`reject-change-button-${a.id}`}>
                      <X className="mr-1 h-4 w-4" /> Tolak
                    </Button>
                  </div>
                } />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.filter((a) => a.status !== "pending").length === 0 ? (
            <EmptyState title="Belum ada riwayat keputusan" icon={ShieldCheck} />
          ) : (
            <div className="space-y-3">
              {history.filter((a) => a.status !== "pending").map((a) => <Row key={a.id} a={a} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="conflicts" className="mt-4">
          {pendingConflicts.length === 0 ? (
            <EmptyState title="Tidak ada konflik keluar" description="Konflik muncul saat penghuni keluar lebih awal padahal ada perpanjangan yang sudah lunas." icon={DoorOpen} />
          ) : (
            <div className="space-y-3">
              {pendingConflicts.map((c) => (
                <div key={c.id} className="rounded-xl border border-border p-4" data-testid={`conflict-row-${c.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold">{c.resident_name} — {c.house_name} · Kamar {c.room_number}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{c.project_name} · diajukan {c.requested_by} · {formatTanggalWaktu(c.requested_at)}</div>
                      <div className="mt-1 text-sm"><span className="text-muted-foreground">Rencana keluar: </span>{formatTanggal(c.proposed_checkout_date)}</div>
                      <div className="text-sm"><span className="text-muted-foreground">Alasan: </span>{c.reason}</div>
                      <div className="mt-1 text-xs" style={{ color: "#b91c1c" }}>Ada {c.extension_ids?.length || 0} perpanjangan lunas yang terdampak jika keluar diproses.</div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button size="sm" onClick={() => resolveConflict.mutate({ id: c.id, decision: "void_and_exit" })} disabled={resolveConflict.isPending} data-testid={`conflict-approve-${c.id}`}>Proses Keluar & Batalkan Perpanjangan</Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => resolveConflict.mutate({ id: c.id, decision: "reject" })} disabled={resolveConflict.isPending} data-testid={`conflict-reject-${c.id}`}>Tolak</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tolak Pengajuan</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Berikan alasan penolakan (opsional).</p>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Batal</Button>
            <Button className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
              onClick={() => reject.mutate({ id: rejectTarget.id, reason: rejectReason })} disabled={reject.isPending}
              data-testid="confirm-reject-button">
              Tolak Pengajuan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
